import React from "react";

import ErrorBoundary from "~/common/ErrorBoundary";

/**
 * In one go, establish an error boundary and Suspense around `children`. The
 * `error` and `loading` props are React nodes, displayed as a fallback when
 * `children` has an error or when it's suspended, respectively.
 */
export function Fallbacks({
	loading,
	error,
	children,
}: React.PropsWithChildren<{
	// Prevents passing `undefined`, see here:
	// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/react/index.d.ts#L392
	loading: NonNullable<React.ReactNode> | null;
	error: React.ReactNode;
}>) {
	return (
		<ErrorBoundary fallback={error}>
			<React.Suspense fallback={loading}>{children}</React.Suspense>
		</ErrorBoundary>
	);
}

function getDisplayName<Props>(component: React.ComponentType<Props>): string {
	return component.displayName ?? component.name ?? "Component";
}

/**
 * Create a wrapper component around `Component` that wraps it with Suspense,
 * using `fallback` as the fallback.
 */
export function withSuspense<Props>(
	fallback: NonNullable<React.ReactNode> | null,
	Component: React.ComponentType<Props>,
): React.ComponentType<Props> {
	function ComponentWrapper(props: Props & JSX.IntrinsicAttributes) {
		return (
			<React.Suspense fallback={fallback}>
				<Component {...props} />
			</React.Suspense>
		);
	}

	ComponentWrapper.displayName = `useSuspense$${getDisplayName(Component)}`;
	return ComponentWrapper;
}

/**
 * Create a wrapper component around `Component` that wraps it with Suspense,
 * using `fallback` as the fallback.
 */
export function withForwardedRefSuspense<Props>(
	fallback: NonNullable<React.ReactNode> | null,
	Component: React.ComponentType<Props>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & any> {
	const CompontentWrapper = React.forwardRef(function ComponentWrapper(props: Props & JSX.IntrinsicAttributes, ref) {
		return (
			<React.Suspense fallback={fallback}>
				<Component {...props} ref={ref} />
			</React.Suspense>
		);
	});

	CompontentWrapper.displayName = `useSuspense$${getDisplayName(Component)}`;
	return CompontentWrapper;
}

/**
 * Create a wrapper around `Component` that wraps it with Fallbacks, using
 * `loading` as the suspense fallback and `error` as the ErrorBoundary
 * fallback.
 */
export function withFallbacks<Props>(
	loading: NonNullable<React.ReactNode> | null,
	error: React.ReactNode,
	Component: React.ComponentType<Props>,
) {
	function ComponentWrapper(props: Props & JSX.IntrinsicAttributes) {
		return (
			<Fallbacks loading={loading} error={error}>
				<Component {...props} />
			</Fallbacks>
		);
	}

	ComponentWrapper.displayName = `useFallbacks$${getDisplayName(Component)}`;
	return ComponentWrapper;
}
